import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import testimonials from './testimonials';
import {
  Truck,
  DollarSign,
  Star,
  Recycle,
  Zap,
  Award,
  Home as HomeIcon,
  Building2,
  Factory,
  GraduationCap,
  Laptop,
  CheckCircle2
} from 'lucide-react';

const Home = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    mobile: ''
  });

  // =====================================================
  // Animation Refs
  // =====================================================

  const heroRef = useRef(null);
  const statsRef = useRef(null);
  const featuresRef = useRef(null);
  const testimonialsRef = useRef(null);

  const heroInView = useInView(heroRef, { once: true });
  const statsInView = useInView(statsRef, { once: true });

  // =====================================================
  // Form Handler
  // =====================================================

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'mobile') {
      const numericValue = value.replace(/\D/g, '').slice(0, 10);

      setFormData((prev) => ({
        ...prev,
        [name]: numericValue
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // =====================================================
  // Start Booking
  // =====================================================

  const handleProceed = () => {
    if (
      formData.name &&
      formData.mobile &&
      formData.mobile.length === 10
    ) {
      localStorage.setItem(
        'userData',
        JSON.stringify(formData)
      );

      navigate('/book-appointment');
    } else if (!formData.name || !formData.mobile) {
      alert('Please fill in all required fields');
    } else if (formData.mobile.length !== 10) {
      alert('Please enter a valid 10-digit mobile number');
    }
  };

  // =====================================================
  // Price List
  // =====================================================

  const handlePriceList = () => {
    navigate('/price-list');
  };

  // =====================================================
  // Book Now
  // =====================================================

  const handleBookNow = () => {
    navigate('/book-appointment');
  };

  // =====================================================
  // Animated Counter
  // =====================================================

  const AnimatedCounter = ({
    end,
    duration = 2000,
    suffix = ''
  }) => {
    const [count, setCount] = useState(0);
    const ref = useRef(null);

    const inView = useInView(ref, { once: true });

    useEffect(() => {
      if (!inView) return;

      let startTime;

      const animate = (currentTime) => {
        if (!startTime) {
          startTime = currentTime;
        }

        const progress = Math.min(
          (currentTime - startTime) / duration,
          1
        );

        setCount(Math.floor(progress * end));

        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);
    }, [inView, end, duration]);

    return (
      <span ref={ref}>
        {count.toLocaleString()}
        {suffix}
      </span>
    );
  };

  return (
    <div className="bg-[#F6F8F4]">

      {/* =====================================================
          HERO SECTION
      ====================================================== */}

      <section
        ref={heroRef}
        className="hero-section relative min-h-[calc(100vh-92px)] flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: "url('/hero.png')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >

        {/* Dark Overlay */}

        <div className="absolute inset-0 bg-[#102018]/65"></div>

        {/* Green Overlay */}

        <div className="absolute inset-0 bg-gradient-to-br from-[#102018]/80 via-transparent to-[#2F6F45]/50"></div>

        {/* Hero Content */}

        <div className="container-custom relative z-10 w-full">

          <motion.div
            initial={{ opacity: 0, y: 35 }}
            animate={
              heroInView
                ? { opacity: 1, y: 0 }
                : {}
            }
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center"
          >

            {/* Badge */}

            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={
                heroInView
                  ? { opacity: 1, y: 0 }
                  : {}
              }
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-md text-[#70BD7D] text-sm font-semibold mb-7"
            >
              <Recycle
                className="w-4 h-4 text-[#70BD7D]"
                strokeWidth={2}
              />

              <span>
                India's Trusted Scrap Collection Network
              </span>
            </motion.div>

            {/* Main Heading */}

            <motion.h1
              className="text-4xl md:text-6xl lg:text-7xl font-heading font-bold text-[#102018] leading-[1.05] mb-6"
              initial={{ opacity: 0, y: 25 }}
              animate={
                heroInView
                  ? { opacity: 1, y: 0 }
                  : {}
              }
              transition={{
                duration: 0.8,
                delay: 0.2
              }}
            >
              Transform Your{' '}

              <span className="block bg-gradient-to-r from-[#2F9621] to-[#70BD7D] bg-clip-text text-transparent">
                Scrap Into Cash
              </span>
            </motion.h1>

            {/* Subtitle */}

            <motion.p
              className="text-lg md:text-xl lg:text-2xl text-[#EAF5EC] max-w-2xl mx-auto mb-9 leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={
                heroInView
                  ? { opacity: 1, y: 0 }
                  : {}
              }
              transition={{
                duration: 0.8,
                delay: 0.4
              }}
            >
              India's Most Trusted Scrap Collection Service
            </motion.p>

            {/* Benefits */}

            <motion.div
              className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4 mb-10"
              initial={{ opacity: 0, y: 20 }}
              animate={
                heroInView
                  ? { opacity: 1, y: 0 }
                  : {}
              }
              transition={{
                duration: 0.8,
                delay: 0.6
              }}
            >

              {/* Free Pickup */}

              <div className="flex items-center gap-2 text-white">
                <div className="w-9 h-9 rounded-full bg-[#70BD7D]/15 border border-[#70BD7D]/30 flex items-center justify-center">
                  <Truck
                    className="w-4 h-4 text-[#70BD7D]"
                    strokeWidth={2}
                  />
                </div>

                <span className="font-medium">
                  Free Pickup
                </span>
              </div>

              {/* Instant Payment */}

              <div className="flex items-center gap-2 text-white">
                <div className="w-9 h-9 rounded-full bg-[#70BD7D]/15 border border-[#70BD7D]/30 flex items-center justify-center">
                  <Zap
                    className="w-4 h-4 text-[#70BD7D]"
                    strokeWidth={2}
                  />
                </div>

                <span className="font-medium">
                  Instant Payment
                </span>
              </div>

              {/* Eco Friendly */}

              <div className="flex items-center gap-2 text-white">
                <div className="w-9 h-9 rounded-full bg-[#70BD7D]/15 border border-[#70BD7D]/30 flex items-center justify-center">
                  <Recycle
                    className="w-4 h-4 text-[#70BD7D]"
                    strokeWidth={2}
                  />
                </div>

                <span className="font-medium">
                  Eco-Friendly
                </span>
              </div>

            </motion.div>

            {/* Hero Buttons */}

            <motion.div
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={
                heroInView
                  ? { opacity: 1, y: 0 }
                  : {}
              }
              transition={{
                duration: 0.8,
                delay: 0.8
              }}
            >

              {/* Book Now */}

              <motion.button
                onClick={handleBookNow}
                className="group min-w-[190px] px-7 py-4 rounded-xl bg-[#70BD7D] text-[#102018] font-semibold flex items-center justify-center gap-2 shadow-[0_12px_35px_rgba(112,189,125,0.25)] hover:bg-[#FFFFFF] transition-all duration-300"
                whileHover={{
                  scale: 1.05,
                  y: -2
                }}
                whileTap={{
                  scale: 0.97
                }}
              >
                <Truck
                  className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300"
                  strokeWidth={2}
                />

                <span>
                  Book Now
                </span>
              </motion.button>

              {/* Current Rates */}

              <motion.button
                onClick={handlePriceList}
                className="group min-w-[190px] px-7 py-4 rounded-xl bg-white/10 border border-white/30 backdrop-blur-md text-white font-semibold flex items-center justify-center gap-2 hover:bg-white hover:text-[#102018] transition-all duration-300"
                whileHover={{
                  scale: 1.05,
                  y: -2
                }}
                whileTap={{
                  scale: 0.97
                }}
              >
                <Award
                  className="w-5 h-5"
                  strokeWidth={2}
                />

                <span>
                  View Current Rates
                </span>
              </motion.button>

            </motion.div>

            {/* Trust Line */}

            <motion.div
              className="mt-10 flex flex-wrap items-center justify-center gap-2 text-sm text-[#EAF5EC]/80"
              initial={{ opacity: 0 }}
              animate={
                heroInView
                  ? { opacity: 1 }
                  : {}
              }
              transition={{
                duration: 0.8,
                delay: 1
              }}
            >
              <span className="w-2 h-2 rounded-full bg-[#70BD7D]"></span>

              <span>
                Trusted by 500+ customers
              </span>

              <span className="text-white/30">
                •
              </span>

              <span>
                Fast & Reliable Pickup
              </span>
            </motion.div>

          </motion.div>
        </div>

        {/* Floating Recycle Icon */}

        <motion.div
          className="absolute top-24 right-10 lg:right-20 hidden md:flex"
          animate={{
            y: [0, -12, 0]
          }}
          transition={{
            duration: 3.5,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
        >
          <div className="w-16 h-16 rounded-full bg-white/10 border border-white/20 backdrop-blur-md flex items-center justify-center">
            <Recycle
              className="w-8 h-8 text-[#70BD7D]"
              strokeWidth={2}
            />
          </div>
        </motion.div>

        {/* Floating Truck Icon */}

        <motion.div
          className="absolute bottom-20 left-10 lg:left-20 hidden md:flex"
          animate={{
            y: [0, 10, 0]
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
        >
          <div className="w-14 h-14 rounded-full bg-white/10 border border-white/20 backdrop-blur-md flex items-center justify-center">
            <Truck
              className="w-7 h-7 text-[#70BD7D]"
              strokeWidth={2}
            />
          </div>
        </motion.div>

        {/* Bottom Fade */}

        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#102018]/40 to-transparent pointer-events-none"></div>

      </section>

      {/* =====================================================
          TRUSTED BY
      ====================================================== */}

      <section className="section-padding bg-[#FFFFFF]">
        <div className="container-custom">

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{
              opacity: 1,
              y: 0
            }}
            transition={{
              duration: 0.8
            }}
            viewport={{
              once: true
            }}
            className="text-center mb-12"
          >

            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-[#EAF5EC] text-[#2F6F45] text-sm font-semibold mb-4">
              TRUSTED NETWORK
            </span>

            <h2 className="text-3xl font-heading font-bold text-[#102018] mb-4">
              Trusted by{' '}

              <span className="bg-gradient-to-r from-[#2F6F45] to-[#70BD7D] bg-clip-text text-transparent">
                500+
              </span>{' '}

              Happy Customers
            </h2>

            <p className="text-[#3F4D45]">
              Serving households, businesses, and industries across India
            </p>

          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">

            {[
              {
                icon: HomeIcon,
                label: 'Households',
                count: '200+'
              },
              {
                icon: Building2,
                label: 'Businesses',
                count: '150+'
              },
              {
                icon: Factory,
                label: 'Industries',
                count: '80+'
              },
              {
                icon: GraduationCap,
                label: 'Schools & Colleges',
                count: '20+'
              }
            ].map((item, index) => {

              const Icon = item.icon;

              return (
                <motion.div
                  key={index}
                  className="group relative text-center p-6 rounded-2xl bg-[#FFFFFF] border border-[#DDE8DF] hover:border-[#70BD7D] hover:shadow-[0_15px_35px_rgba(16,32,24,0.08)] transition-all duration-300 overflow-hidden"
                  initial={{
                    opacity: 0,
                    y: 20
                  }}
                  whileInView={{
                    opacity: 1,
                    y: 0
                  }}
                  transition={{
                    duration: 0.5,
                    delay: index * 0.1
                  }}
                  viewport={{
                    once: true
                  }}
                  whileHover={{
                    y: -6
                  }}
                >

                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#2F6F45] to-[#70BD7D] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                  <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-[#EAF5EC] flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <Icon
                      className="w-7 h-7 text-[#2F6F45]"
                      strokeWidth={2}
                    />
                  </div>

                  <h3 className="text-base font-heading font-semibold text-[#102018] mb-1">
                    {item.label}
                  </h3>

                  <p className="text-[#2F6F45] font-bold">
                    {item.count}
                  </p>

                </motion.div>
              );
            })}

          </div>

        </div>
      </section>

      {/* =====================================================
          IMPACT STATS
      ====================================================== */}

      <section
        ref={statsRef}
        className="relative overflow-hidden section-padding bg-[#102018] text-white"
      >

        <div className="absolute inset-0 bg-gradient-to-br from-[#102018] via-[#102018] to-[#2F6F45]"></div>

        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#70BD7D]/10 blur-3xl"></div>

        <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-[#2F6F45]/20 blur-3xl"></div>

        <div className="container-custom relative z-10">

          <motion.div
            initial={{
              opacity: 0,
              y: 20
            }}
            animate={
              statsInView
                ? {
                    opacity: 1,
                    y: 0
                  }
                : {}
            }
            transition={{
              duration: 0.8
            }}
            className="text-center mb-10"
          >

            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-white/10 border border-white/10 text-[#70BD7D] text-sm font-semibold mb-4">
              OUR IMPACT
            </span>

            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-3">
              Making a Difference
            </h2>

            <p className="text-[#EAF5EC] max-w-xl mx-auto">
              Making a difference, one pickup at a time
            </p>

          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">

            {[
              {
                number: 500,
                suffix: '+',
                label: 'Successful Pickups',
                icon: Truck
              },
              {
                number: 50,
                suffix: ' kg',
                label: 'Recycled Materials',
                icon: Recycle
              },
              {
                number: 100,
                suffix: '%',
                label: 'Customer Satisfaction',
                icon: Star
              }
            ].map((stat, index) => {

              const Icon = stat.icon;

              return (
                <motion.div
                  key={index}
                  className="group relative text-center p-7 rounded-2xl bg-white/[0.07] border border-white/10 backdrop-blur-sm hover:bg-white/[0.11] hover:border-[#70BD7D]/40 transition-all duration-300"
                  initial={{
                    opacity: 0,
                    scale: 0.9
                  }}
                  animate={
                    statsInView
                      ? {
                          opacity: 1,
                          scale: 1
                        }
                      : {}
                  }
                  transition={{
                    duration: 0.5,
                    delay: index * 0.2
                  }}
                  whileHover={{
                    y: -5
                  }}
                >

                  <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-[#70BD7D]/10 border border-[#70BD7D]/20 flex items-center justify-center">

                    <Icon
                      className="w-7 h-7 text-[#70BD7D]"
                      strokeWidth={2}
                    />

                  </div>

                  <div className="text-4xl font-heading font-bold mb-2">
                    <AnimatedCounter
                      end={stat.number}
                      suffix={stat.suffix}
                    />
                  </div>

                  <p className="text-[#EAF5EC] font-medium">
                    {stat.label}
                  </p>

                </motion.div>
              );
            })}

          </div>

        </div>
      </section>

      {/* =====================================================
          WHY CHOOSE US
      ====================================================== */}

      <section
        ref={featuresRef}
        className="section-padding bg-[#F6F8F4]"
      >

        <div className="container-custom">

          <motion.div
            initial={{
              opacity: 0,
              y: 20
            }}
            whileInView={{
              opacity: 1,
              y: 0
            }}
            transition={{
              duration: 0.8
            }}
            viewport={{
              once: true
            }}
            className="text-center mb-12"
          >

            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-[#EAF5EC] text-[#2F6F45] text-sm font-semibold mb-4">
              WHY SCRAPSECTOR
            </span>

            <h2 className="text-3xl md:text-4xl font-heading font-bold text-[#102018] mb-4">
              Why Choose{' '}

              <span className="bg-gradient-to-r from-[#2F6F45] to-[#70BD7D] bg-clip-text text-transparent">
                ScrapSector?
              </span>
            </h2>

            <p className="text-[#3F4D45] max-w-2xl mx-auto">
              We're committed to providing the best scrap collection experience with eco-friendly practices and fair pricing.
            </p>

          </motion.div>

          <div className="grid md:grid-cols-3 gap-5">

            {[
              {
                icon: Truck,
                title: 'Doorstep Convenience',
                description:
                  'Free pickup service right at your doorstep. No need to carry heavy scrap items anywhere!'
              },
              {
                icon: Recycle,
                title: 'Eco-Friendly Mission',
                description:
                  'We ensure 100% responsible recycling and disposal, helping protect our environment.'
              },
              {
                icon: DollarSign,
                title: 'Best Market Rates',
                description:
                  'Get the highest prices for your scrap based on current market rates and quality assessment.'
              }
            ].map((feature, index) => {

              const Icon = feature.icon;

              return (
                <motion.div
                  key={index}
                  className="group relative"
                  initial={{
                    opacity: 0,
                    y: 20
                  }}
                  whileInView={{
                    opacity: 1,
                    y: 0
                  }}
                  transition={{
                    duration: 0.5,
                    delay: index * 0.1
                  }}
                  viewport={{
                    once: true
                  }}
                >

                  <div className="h-full bg-[#FFFFFF] rounded-2xl border border-[#DDE8DF] p-7 hover:border-[#70BD7D] hover:shadow-[0_18px_40px_rgba(16,32,24,0.08)] transition-all duration-300">

                    <div className="flex items-center justify-between mb-6">

                      <div className="w-14 h-14 rounded-2xl bg-[#EAF5EC] flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
                        <Icon
                          className="w-7 h-7 text-[#2F6F45]"
                          strokeWidth={2}
                        />
                      </div>

                      <span className="text-sm font-semibold text-[#DDE8DF]">
                        0{index + 1}
                      </span>

                    </div>

                    <h3 className="text-xl font-heading font-semibold text-[#102018] mb-3">
                      {feature.title}
                    </h3>

                    <p className="text-[#3F4D45] leading-relaxed">
                      {feature.description}
                    </p>

                    <div className="mt-6 h-1 w-12 rounded-full bg-[#2F6F45] group-hover:w-20 transition-all duration-300"></div>

                  </div>

                </motion.div>
              );
            })}

          </div>

        </div>
      </section>

      {/* =====================================================
          SERVICES
      ====================================================== */}

      <section className="section-padding bg-[#FFFFFF]">

        <div className="container-custom">

          <motion.div
            initial={{
              opacity: 0,
              y: 20
            }}
            whileInView={{
              opacity: 1,
              y: 0
            }}
            transition={{
              duration: 0.8
            }}
            viewport={{
              once: true
            }}
            className="text-center mb-12"
          >

            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-[#EAF5EC] text-[#2F6F45] text-sm font-semibold mb-4">
              OUR SERVICES
            </span>

            <h2 className="text-3xl md:text-4xl font-heading font-bold text-[#102018] mb-4">
              Everything You Need,

              <span className="block bg-gradient-to-r from-[#2F6F45] to-[#70BD7D] bg-clip-text text-transparent">
                In One Place
              </span>
            </h2>

            <p className="text-[#3F4D45] max-w-2xl mx-auto">
              Comprehensive scrap collection solutions for every need
            </p>

          </motion.div>

          <div className="grid md:grid-cols-3 gap-5">

            {[
              {
                icon: HomeIcon,
                title: 'Household Scrap',
                description:
                  'Perfect for homes - collect paper, plastic, metal, and e-waste from your doorstep',
                features: [
                  'Paper & Cardboard',
                  'Plastic Items',
                  'Metal Scrap',
                  'E-Waste'
                ]
              },
              {
                icon: Building2,
                title: 'Business Solutions',
                description:
                  'Bulk collection for offices, shops, and commercial establishments with regular schedules',
                features: [
                  'Regular Pickups',
                  'Bulk Collection',
                  'Commercial Rates',
                  'Scheduled Service'
                ]
              },
              {
                icon: Laptop,
                title: 'E-Waste Specialists',
                description:
                  'Secure disposal of electronics with data destruction and certified recycling',
                features: [
                  'Data Destruction',
                  'Certified Recycling',
                  'Secure Handling',
                  'Compliance'
                ]
              }
            ].map((service, index) => {

              const Icon = service.icon;

              return (
                <motion.div
                  key={index}
                  className="group relative"
                  initial={{
                    opacity: 0,
                    y: 20
                  }}
                  whileInView={{
                    opacity: 1,
                    y: 0
                  }}
                  transition={{
                    duration: 0.5,
                    delay: index * 0.1
                  }}
                  viewport={{
                    once: true
                  }}
                >

                  <div className="h-full rounded-2xl bg-[#F6F8F4] border border-[#DDE8DF] p-7 hover:bg-[#FFFFFF] hover:border-[#70BD7D] hover:shadow-[0_18px_40px_rgba(16,32,24,0.08)] transition-all duration-300">

                    <div className="flex items-start justify-between mb-6">

                      <div className="w-14 h-14 rounded-2xl bg-[#EAF5EC] flex items-center justify-center group-hover:scale-110 transition-transform duration-300">

                        <Icon
                          className="w-7 h-7 text-[#2F6F45]"
                          strokeWidth={2}
                        />

                      </div>

                      <span className="text-xs font-bold tracking-widest text-[#718078]">
                        SERVICE 0{index + 1}
                      </span>

                    </div>

                    <h3 className="text-xl font-heading font-semibold text-[#102018] mb-3">
                      {service.title}
                    </h3>

                    <p className="text-[#3F4D45] mb-6 leading-relaxed">
                      {service.description}
                    </p>

                    <ul className="space-y-3">

                      {service.features.map(
                        (feature, featureIndex) => (

                          <li
                            key={featureIndex}
                            className="flex items-center space-x-3 text-sm text-[#3F4D45]"
                          >

                            <CheckCircle2
                              className="w-5 h-5 text-[#2F6F45] flex-shrink-0"
                              strokeWidth={2}
                            />

                            <span>
                              {feature}
                            </span>

                          </li>

                        )
                      )}

                    </ul>

                  </div>

                </motion.div>
              );
            })}

          </div>

        </div>
      </section>

      {/* =====================================================
          TESTIMONIALS
      ====================================================== */}

      <section
        ref={testimonialsRef}
        className="section-padding bg-[#F6F8F4]"
      >
        <div className="container-custom">

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{
              opacity: 1,
              y: 0
            }}
            transition={{
              duration: 0.8
            }}
            viewport={{
              once: true
            }}
            className="text-center mb-12"
          >
            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-[#EAF5EC] text-[#2F6F45] text-sm font-semibold mb-4">
              CUSTOMER STORIES
            </span>

            <h2 className="text-3xl md:text-4xl font-heading font-bold text-[#102018] mb-4">
              What Our{' '}
              <span className="bg-gradient-to-r from-[#2F6F45] to-[#70BD7D] bg-clip-text text-transparent">
                Customers Say
              </span>
            </h2>

            <p className="text-[#3F4D45]">
              Real feedback from satisfied customers across India
            </p>
          </motion.div>

          {/* Testimonials */}

          <div className="grid md:grid-cols-3 gap-5">

            {testimonials
              .filter(
                (testimonial) => testimonial.rating === 5
              )
              .slice(0, 3)
              .map((testimonial, index) => (

                <motion.div
                  key={index}
                  className="group relative bg-[#FFFFFF] rounded-2xl border border-[#DDE8DF] p-7 hover:border-[#70BD7D] hover:shadow-[0_18px_40px_rgba(16,32,24,0.08)] transition-all duration-300"
                  initial={{
                    opacity: 0,
                    y: 20
                  }}
                  whileInView={{
                    opacity: 1,
                    y: 0
                  }}
                  transition={{
                    duration: 0.5,
                    delay: Math.min(index * 0.05, 0.4)
                  }}
                  viewport={{
                    once: true
                  }}
                >

                  <div className="absolute top-5 right-6 text-5xl font-serif text-[#EAF5EC] group-hover:text-[#70BD7D]/30 transition-colors">
                    "
                  </div>

                  {/* Rating */}

                  <div className="flex items-center mb-5">
                    {[...Array(testimonial.rating)].map(
                      (_, i) => (
                        <Star
                          key={i}
                          className="w-4 h-4 text-[#2F6F45] fill-current mr-1"
                          strokeWidth={2}
                        />
                      )
                    )}
                  </div>

                  {/* Review */}

                  <p className="text-[#3F4D45] mb-7 leading-relaxed">
                    "{testimonial.text}"
                  </p>

                  {/* Customer */}

                  <div className="flex items-center pt-5 border-t border-[#DDE8DF]">

                    <div className="w-11 h-11 rounded-xl bg-[#EAF5EC] border border-[#DDE8DF] flex items-center justify-center text-[#2F6F45] font-bold">
                      {testimonial.name.charAt(0)}
                    </div>

                    <div className="ml-3">

                      <p className="font-semibold text-[#102018]">
                        {testimonial.name}
                      </p>

                      <p className="text-sm text-[#3F4D45]">
                        {testimonial.location}
                      </p>

                    </div>

                  </div>

                </motion.div>

              ))}

          </div>

          {/* See More */}

          <div className="flex justify-center mt-10">

            <motion.button
              onClick={() => navigate('/customer-ratings')}
              whileHover={{
                scale: 1.03,
                y: -2
              }}
              whileTap={{
                scale: 0.97
              }}
              className="px-7 py-3 rounded-xl bg-[#2F6F45] text-white font-semibold hover:bg-[#102018] transition-all duration-300 shadow-[0_10px_25px_rgba(47,111,69,0.18)]"
            >
              See More
            </motion.button>

          </div>

        </div>
      </section>

      {/* =====================================================
          FAQ
      ====================================================== */}

      <section className="section-padding bg-[#FFFFFF]">

        <div className="container-custom">

          <motion.div
            initial={{
              opacity: 0,
              y: 20
            }}
            whileInView={{
              opacity: 1,
              y: 0
            }}
            transition={{
              duration: 0.8
            }}
            viewport={{
              once: true
            }}
            className="text-center mb-12"
          >

            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-[#EAF5EC] text-[#2F6F45] text-sm font-semibold mb-4">
              FAQ
            </span>

            <h2 className="text-3xl md:text-4xl font-heading font-bold text-[#102018] mb-4">
              Frequently Asked{' '}

              <span className="bg-gradient-to-r from-[#2F6F45] to-[#70BD7D] bg-clip-text text-transparent">
                Questions
              </span>
            </h2>

            <p className="text-[#3F4D45]">
              Everything you need to know about our services
            </p>

          </motion.div>

          <div className="max-w-3xl mx-auto space-y-3">

            {[
              {
                question:
                  'How do I schedule a scrap pickup?',
                answer:
                  'Simply fill out the form above with your name and mobile number, then follow the booking process to select your preferred date and time. It takes less than 2 minutes!'
              },
              {
                question:
                  'Is the pickup service really free?',
                answer:
                  'Yes! Our pickup service is completely free. We only charge for the scrap materials we collect, and you get paid for those materials at market rates.'
              },
              {
                question:
                  'What types of scrap do you accept?',
                answer:
                  'We accept paper, plastic, metal, glass, e-waste, and textile scrap. Check our price list for detailed categories and current rates.'
              },
              {
                question:
                  'How do you determine the price?',
                answer:
                  'Prices are based on current market rates, quality of materials, and quantity. Our team weighs and assesses your scrap items during pickup.'
              },
              {
                question:
                  'What is the minimum pickup value?',
                answer:
                  'We have a minimum pickup value of ₹250 to ensure our service is cost-effective for both you and us.'
              },
              {
                question:
                  'How quickly will I get paid?',
                answer:
                  'You get paid instantly! We make payment immediately after weighing and assessing your scrap items via UPI, cash, or bank transfer.'
              }
            ].map((faq, index) => (

              <motion.div
                key={index}
                className="group bg-[#F6F8F4] rounded-2xl border border-[#DDE8DF] hover:border-[#70BD7D] transition-all duration-300 overflow-hidden"
                initial={{
                  opacity: 0,
                  y: 20
                }}
                whileInView={{
                  opacity: 1,
                  y: 0
                }}
                transition={{
                  duration: 0.5,
                  delay: index * 0.1
                }}
                viewport={{
                  once: true
                }}
              >

                <details className="group">

                  <summary className="flex items-center justify-between cursor-pointer px-6 py-5 text-base md:text-lg font-heading font-semibold text-[#102018] hover:text-[#2F6F45] transition-colors duration-200 list-none">

                    <span>
                      {faq.question}
                    </span>

                    <div className="w-8 h-8 rounded-full bg-[#EAF5EC] flex items-center justify-center flex-shrink-0 ml-4 group-open:rotate-180 transition-transform duration-200">

                      <svg
                        className="w-4 h-4 text-[#2F6F45]"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>

                    </div>

                  </summary>

                  <div className="px-6 pb-5">

                    <div className="pt-4 border-t border-[#DDE8DF]">

                      <p className="text-[#3F4D45] leading-relaxed">
                        {faq.answer}
                      </p>

                    </div>

                  </div>

                </details>

              </motion.div>

            ))}

          </div>

        </div>
      </section>

      {/* =====================================================
          SEO HIDDEN KEYWORDS
      ====================================================== */}

      <div style={{ display: 'none' }}>
        Scrap pickup service in Panchkula, Kalka, Pinjore and Chandigarh.
        Kabadiwala near me, feriwala scrap buyer, cash for scrap, recycle paper,
        plastic, metal and e-waste.
      </div>

      {/* =====================================================
          MOBILE HERO IMAGE ONLY
      ====================================================== */}

      <style>
        {`
          @media (max-width: 768px) {
            .hero-section {
              background-image: url('/hero-mobile.png') !important;
              background-size: cover !important;
              background-position: center center !important;
              background-repeat: no-repeat !important;
            }
          }
        `}
      </style>

    </div>
  );
};

export default Home;